import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import type {
  ResourceKind,
  ResourceLifecycleStatus,
  ResourceRelationshipType,
  ResourceTopologyImportMode,
  ResourceTopologyImportPayload,
  ResourceTopologyImportPreview,
  ResourceTopologyImportScope,
  ResourceNode,
  ResourceRelationship,
} from '../../types/prodResourceTopology';

export const IMPORT_CONTROL_SHEET = '导入控制';
export const RESOURCE_IMPORT_SHEET = '资源节点';
export const RELATIONSHIP_IMPORT_SHEET = '资源关系';
export const IMPORT_DICTIONARY_SHEET = '模板字典';
export const MAX_TOPOLOGY_IMPORT_FILE_BYTES = 5 * 1024 * 1024;
export const MAX_TOPOLOGY_IMPORT_ROWS = 5_000;

export interface ResourceTopologyImportParseIssue {
  id: string;
  sheet: string;
  rowNumber: number | null;
  constraintCode: string;
  message: string;
}

export interface ParsedResourceTopologyImport {
  payload: ResourceTopologyImportPayload;
  issues: ResourceTopologyImportParseIssue[];
}

export const isResourceTopologyImportPreviewCommittable = (
  preview: ResourceTopologyImportPreview | null,
  currentRevision: number,
): boolean => Boolean(
  preview
  && preview.previewToken
  && preview.baseRevision === currentRevision
  && preview.summary.errorCount === 0
  && preview.rows.every((row) => row.status !== 'ERROR')
  && preview.issues.every((item) => item.hardOrSoft !== 'hard')
  && preview.summary.createNodes
    + preview.summary.updateNodes
    + preview.summary.createRelationships
    + preview.summary.updateRelationships > 0,
);

type SheetRow = Record<string, unknown> & { __rowNum__?: number };

type WorksheetDataValidation = {
  type: 'list' | 'textLength' | 'whole';
  operator?: 'between';
  allowBlank: boolean;
  formulae: Array<string | number>;
};

type WorksheetWithDataValidations = {
  dataValidations: {
    add: (range: string, validation: WorksheetDataValidation) => void;
  };
};

const addDataValidation = (
  worksheet: ExcelJS.Worksheet,
  range: string,
  validation: WorksheetDataValidation,
): void => {
  (worksheet as unknown as WorksheetWithDataValidations).dataValidations.add(range, validation);
};

const RESOURCE_HEADERS = [
  '资源编码', '资源名称', '资源类型', '生命周期', '可作为CIP回路成员', '备注',
];
const RELATIONSHIP_HEADERS = ['关系类型', '起点编码', '目标编码', '优先级', '备注'];
const RESOURCE_KINDS: ResourceKind[] = [
  'BUILDING', 'AREA', 'SUITE', 'ROOM', 'EQUIPMENT', 'COMPONENT', 'CIP_STATION',
];
const LIFECYCLE_STATUSES: Array<Exclude<ResourceLifecycleStatus, 'RETIRED'>> = ['ACTIVE', 'INACTIVE'];
const RELATIONSHIP_TYPES: ResourceRelationshipType[] = [
  'PARENT_OF',
];
const IMPORT_MODES: ResourceTopologyImportMode[] = ['APPEND', 'REPLACE'];
const IMPORT_SCOPES: ResourceTopologyImportScope[] = ['FULL', 'RESOURCES', 'RELATIONSHIPS'];
const EXPORTABLE_RESOURCE_KINDS = new Set<ResourceKind>(RESOURCE_KINDS);

const text = (value: unknown): string => String(value ?? '').trim();
const upper = (value: unknown): string => text(value).toUpperCase();

const valueOf = (row: SheetRow, chineseHeader: string, englishHeader: string): unknown => (
  row[chineseHeader] ?? row[englishHeader] ?? ''
);

const parseBoolean = (value: unknown): boolean | null => {
  const normalized = upper(value);
  if (!normalized) return false;
  if (['是', 'TRUE', '1', 'Y', 'YES'].includes(normalized)) return true;
  if (['否', 'FALSE', '0', 'N', 'NO'].includes(normalized)) return false;
  return null;
};

const issue = (
  issues: ResourceTopologyImportParseIssue[],
  sheet: string,
  rowNumber: number | null,
  constraintCode: string,
  message: string,
) => {
  issues.push({
    id: `${sheet}-${rowNumber ?? 'sheet'}-${constraintCode}-${issues.length}`,
    sheet,
    rowNumber,
    constraintCode,
    message,
  });
};

const cellAddresses = (sheet: XLSX.WorkSheet): string[] => (
  Object.keys(sheet).filter((key) => !key.startsWith('!') && /^[A-Z]+\d+$/.test(key))
);

const formattedCellValue = (cell: XLSX.CellObject | undefined): string => (
  cell ? XLSX.utils.format_cell(cell) : ''
);

const readControlValue = (sheet: XLSX.WorkSheet, address: string): string => (
  upper(formattedCellValue(sheet[address]))
);

const parseWorkbookControl = (
  sheet: XLSX.WorkSheet | undefined,
  issues: ResourceTopologyImportParseIssue[],
): Pick<ResourceTopologyImportPayload, 'mode' | 'scope'> => {
  if (!sheet) return { mode: 'APPEND', scope: 'FULL' };
  const mode = readControlValue(sheet, 'B2') || 'APPEND';
  const scope = readControlValue(sheet, 'B3') || 'FULL';
  if (!IMPORT_MODES.includes(mode as ResourceTopologyImportMode)) {
    issue(issues, IMPORT_CONTROL_SHEET, 2, 'IMPORT_MODE_INVALID', '导入方式只能是 APPEND 或 REPLACE');
  }
  if (!IMPORT_SCOPES.includes(scope as ResourceTopologyImportScope)) {
    issue(issues, IMPORT_CONTROL_SHEET, 3, 'IMPORT_SCOPE_INVALID', '导入范围只能是 FULL、RESOURCES 或 RELATIONSHIPS');
  }
  return {
    mode: IMPORT_MODES.includes(mode as ResourceTopologyImportMode) ? mode as ResourceTopologyImportMode : 'APPEND',
    scope: IMPORT_SCOPES.includes(scope as ResourceTopologyImportScope) ? scope as ResourceTopologyImportScope : 'FULL',
  };
};

const parseRows = (sheet: XLSX.WorkSheet, headers: string[]): SheetRow[] => {
  const rows = new Set<number>();
  cellAddresses(sheet).forEach((address) => {
    const position = XLSX.utils.decode_cell(address);
    if (position.r > 0 && position.c < headers.length) rows.add(position.r);
  });
  return Array.from(rows)
    .sort((a, b) => a - b)
    .map((rowNumber) => {
      const row: SheetRow = { __rowNum__: rowNumber };
      headers.forEach((header, column) => {
        row[header] = formattedCellValue(sheet[XLSX.utils.encode_cell({ r: rowNumber, c: column })]);
      });
      return row;
    });
};

const validateHeaders = (
  sheet: XLSX.WorkSheet,
  expected: string[],
  sheetName: string,
  issues: ResourceTopologyImportParseIssue[],
) => {
  const headers = expected.map((_, column) => (
    text(formattedCellValue(sheet[XLSX.utils.encode_cell({ r: 0, c: column })]))
  ));
  const hasUnexpectedHeader = cellAddresses(sheet).some((address) => {
    const position = XLSX.utils.decode_cell(address);
    return position.r === 0
      && position.c >= expected.length
      && Boolean(text(formattedCellValue(sheet[address])));
  });
  if (hasUnexpectedHeader || expected.some((header, index) => headers[index] !== header)) {
    issue(
      issues,
      sheetName,
      1,
      'IMPORT_HEADERS_INVALID',
      `表头必须严格为：${expected.join('、')}`,
    );
  }
  const firstOutsideCell = cellAddresses(sheet).find((address) => {
    const position = XLSX.utils.decode_cell(address);
    return position.r > 0
      && position.c >= expected.length
      && Boolean(text(formattedCellValue(sheet[address])));
  });
  if (firstOutsideCell) {
    issue(
      issues,
      sheetName,
      XLSX.utils.decode_cell(firstOutsideCell).r + 1,
      'IMPORT_CELL_OUTSIDE_HEADERS',
      `单元格 ${firstOutsideCell} 位于固定表头之外，不能静默忽略`,
    );
  }
};

const rejectFormulaCells = (
  sheet: XLSX.WorkSheet,
  sheetName: string,
  issues: ResourceTopologyImportParseIssue[],
) => {
  let reported = 0;
  let total = 0;
  cellAddresses(sheet).forEach((address) => {
    if (sheet[address]?.f) {
      total += 1;
    }
    if (sheet[address]?.f && reported < 100) {
      issue(
        issues,
        sheetName,
        XLSX.utils.decode_cell(address).r + 1,
        'IMPORT_FORMULA_NOT_ALLOWED',
        `单元格 ${address} 含公式；导入文件只能包含静态值`,
      );
      reported += 1;
    }
  });
  if (total > reported) {
    issue(
      issues,
      sheetName,
      null,
      'IMPORT_FORMULA_ERRORS_TRUNCATED',
      `共发现 ${total} 个公式单元格，仅逐行列出前 ${reported} 个`,
    );
  }
};

const rejectNumericIdentifierCells = (
  sheet: XLSX.WorkSheet,
  sheetName: string,
  identifierColumns: number[],
  issues: ResourceTopologyImportParseIssue[],
) => {
  cellAddresses(sheet).forEach((address) => {
    const position = XLSX.utils.decode_cell(address);
    if (position.r > 0 && identifierColumns.includes(position.c) && sheet[address]?.t === 'n') {
      issue(
        issues,
        sheetName,
        position.r + 1,
        'IMPORT_IDENTIFIER_CELL_TYPE_INVALID',
        `单元格 ${address} 必须使用文本格式，避免编码前导零丢失`,
      );
    }
  });
};

export const parseResourceTopologyImportWorkbook = (workbook: XLSX.WorkBook): ParsedResourceTopologyImport => {
  const issues: ResourceTopologyImportParseIssue[] = [];
  const unexpectedSheets = workbook.SheetNames.filter(
    (name) => !['导入说明', IMPORT_CONTROL_SHEET, RESOURCE_IMPORT_SHEET, RELATIONSHIP_IMPORT_SHEET, IMPORT_DICTIONARY_SHEET].includes(name),
  );
  unexpectedSheets.forEach((name) => {
    issue(issues, name, null, 'IMPORT_UNEXPECTED_SHEET', `不支持的工作表：${name}`);
  });
  const resourceSheet = workbook.Sheets[RESOURCE_IMPORT_SHEET];
  const relationshipSheet = workbook.Sheets[RELATIONSHIP_IMPORT_SHEET];
  const control = parseWorkbookControl(workbook.Sheets[IMPORT_CONTROL_SHEET], issues);
  if (!resourceSheet) {
    issue(issues, RESOURCE_IMPORT_SHEET, null, 'IMPORT_RESOURCE_SHEET_MISSING', `缺少“${RESOURCE_IMPORT_SHEET}”工作表`);
  }
  if (!relationshipSheet) {
    issue(issues, RELATIONSHIP_IMPORT_SHEET, null, 'IMPORT_RELATIONSHIP_SHEET_MISSING', `缺少“${RELATIONSHIP_IMPORT_SHEET}”工作表`);
  }

  if (resourceSheet) {
    validateHeaders(resourceSheet, RESOURCE_HEADERS, RESOURCE_IMPORT_SHEET, issues);
    rejectFormulaCells(resourceSheet, RESOURCE_IMPORT_SHEET, issues);
    rejectNumericIdentifierCells(resourceSheet, RESOURCE_IMPORT_SHEET, [0], issues);
  }
  if (relationshipSheet) {
    validateHeaders(relationshipSheet, RELATIONSHIP_HEADERS, RELATIONSHIP_IMPORT_SHEET, issues);
    rejectFormulaCells(relationshipSheet, RELATIONSHIP_IMPORT_SHEET, issues);
    rejectNumericIdentifierCells(relationshipSheet, RELATIONSHIP_IMPORT_SHEET, [1, 2], issues);
  }

  const allResourceRows = resourceSheet ? parseRows(resourceSheet, RESOURCE_HEADERS) : [];
  const allRelationshipRows = relationshipSheet ? parseRows(relationshipSheet, RELATIONSHIP_HEADERS) : [];
  if (allResourceRows.length + allRelationshipRows.length > MAX_TOPOLOGY_IMPORT_ROWS) {
    issue(
      issues,
      '工作簿',
      null,
      'IMPORT_ROW_LIMIT_EXCEEDED',
      `资源节点和资源关系合计不能超过 ${MAX_TOPOLOGY_IMPORT_ROWS} 行`,
    );
  }
  const resourceRows = allResourceRows.slice(0, MAX_TOPOLOGY_IMPORT_ROWS);
  const relationshipRows = allRelationshipRows.slice(
    0,
    Math.max(0, MAX_TOPOLOGY_IMPORT_ROWS - resourceRows.length),
  );
  const resources: ResourceTopologyImportPayload['resources'] = [];
  const relationships: ResourceTopologyImportPayload['relationships'] = [];
  const seenResourceCodes = new Set<string>();
  const seenRelationships = new Set<string>();

  resourceRows.forEach((row, index) => {
    const rowNumber = typeof row.__rowNum__ === 'number' ? row.__rowNum__ + 1 : index + 2;
    const issueCountBefore = issues.length;
    const code = text(valueOf(row, '资源编码', 'code'));
    const name = text(valueOf(row, '资源名称', 'name'));
    const kindValue = upper(valueOf(row, '资源类型', 'kind'));
    const lifecycleValue = upper(valueOf(row, '生命周期', 'lifecycleStatus')) || 'ACTIVE';
    const cipCleanable = parseBoolean(valueOf(row, '可作为CIP回路成员', 'cipCleanable'));
    const note = text(valueOf(row, '备注', 'note')) || null;
    const supportsEquipmentAttributes = kindValue === 'EQUIPMENT' || kindValue === 'COMPONENT';

    if (!code) issue(issues, RESOURCE_IMPORT_SHEET, rowNumber, 'IMPORT_RESOURCE_CODE_REQUIRED', '资源编码不能为空');
    if (!name) issue(issues, RESOURCE_IMPORT_SHEET, rowNumber, 'IMPORT_RESOURCE_NAME_REQUIRED', '资源名称不能为空');
    if (!RESOURCE_KINDS.includes(kindValue as ResourceKind)) {
      issue(issues, RESOURCE_IMPORT_SHEET, rowNumber, 'IMPORT_RESOURCE_KIND_INVALID', `不支持的资源类型：${kindValue || '空'}`);
    }
    if (!LIFECYCLE_STATUSES.includes(lifecycleValue as Exclude<ResourceLifecycleStatus, 'RETIRED'>)) {
      issue(issues, RESOURCE_IMPORT_SHEET, rowNumber, 'IMPORT_RESOURCE_LIFECYCLE_INVALID', `生命周期只能是 ACTIVE 或 INACTIVE`);
    }
    if (cipCleanable === null) {
      issue(issues, RESOURCE_IMPORT_SHEET, rowNumber, 'IMPORT_CIP_CLEANABLE_INVALID', '可作为CIP回路成员只能填写是/否或 TRUE/FALSE');
    }
    if (!supportsEquipmentAttributes && cipCleanable === true) {
      issue(
        issues,
        RESOURCE_IMPORT_SHEET,
        rowNumber,
        'IMPORT_RESOURCE_ATTRIBUTE_KIND_INVALID',
        '只有 EQUIPMENT 或 COMPONENT 可以填写 CIP 清洗拓扑',
      );
    }
    const normalizedCode = code.toLocaleUpperCase();
    if (code && seenResourceCodes.has(normalizedCode)) {
      issue(issues, RESOURCE_IMPORT_SHEET, rowNumber, 'IMPORT_RESOURCE_CODE_DUPLICATE_FILE', `文件内资源编码重复：${code}`);
    }
    if (code) seenResourceCodes.add(normalizedCode);

    if (issues.length === issueCountBefore) {
      const kind = kindValue as ResourceKind;
      resources.push({
        rowNumber,
        code,
        name,
        kind,
        lifecycleStatus: lifecycleValue as Exclude<ResourceLifecycleStatus, 'RETIRED'>,
        attributes: supportsEquipmentAttributes
          ? { cipCleanable: cipCleanable === true }
          : null,
        note,
      });
    }
  });

  relationshipRows.forEach((row, index) => {
    const rowNumber = typeof row.__rowNum__ === 'number' ? row.__rowNum__ + 1 : index + 2;
    const issueCountBefore = issues.length;
    const relationTypeValue = upper(valueOf(row, '关系类型', 'relationType'));
    const sourceCode = text(valueOf(row, '起点编码', 'sourceCode'));
    const targetCode = text(valueOf(row, '目标编码', 'targetCode'));
    const priorityText = text(valueOf(row, '优先级', 'priority'));
    const note = text(valueOf(row, '备注', 'note')) || null;
    let priority: number | null = priorityText ? Number(priorityText) : null;

    if (!RELATIONSHIP_TYPES.includes(relationTypeValue as ResourceRelationshipType)) {
      issue(issues, RELATIONSHIP_IMPORT_SHEET, rowNumber, 'IMPORT_RELATION_TYPE_INVALID', `不支持的关系类型：${relationTypeValue || '空'}`);
    }
    if (!sourceCode) issue(issues, RELATIONSHIP_IMPORT_SHEET, rowNumber, 'IMPORT_RELATION_SOURCE_REQUIRED', '起点编码不能为空');
    if (!targetCode) issue(issues, RELATIONSHIP_IMPORT_SHEET, rowNumber, 'IMPORT_RELATION_TARGET_REQUIRED', '目标编码不能为空');
    if (
      sourceCode
      && targetCode
      && sourceCode.toLocaleUpperCase() === targetCode.toLocaleUpperCase()
    ) {
      issue(issues, RELATIONSHIP_IMPORT_SHEET, rowNumber, 'RESOURCE_RELATION_SELF_REFERENCE', '关系起点和目标不能相同');
    }
    if (relationTypeValue === 'CIP_BACKUP_STATION') {
      if (priority !== null && (!Number.isInteger(priority) || priority <= 0)) {
        issue(issues, RELATIONSHIP_IMPORT_SHEET, rowNumber, 'CIP_BACKUP_PRIORITY_INVALID', '备站优先级必须是正整数');
      }
    } else if (priority !== null) {
      issue(issues, RELATIONSHIP_IMPORT_SHEET, rowNumber, 'RESOURCE_RELATION_PRIORITY_NOT_ALLOWED', '只有回路备站关系可以填写优先级');
    }
    const relationshipKey = `${relationTypeValue}|${sourceCode.toLocaleUpperCase()}|${targetCode.toLocaleUpperCase()}`;
    if (sourceCode && targetCode && seenRelationships.has(relationshipKey)) {
      issue(issues, RELATIONSHIP_IMPORT_SHEET, rowNumber, 'IMPORT_RELATIONSHIP_DUPLICATE_FILE', '文件内存在重复资源关系');
    }
    if (sourceCode && targetCode) seenRelationships.add(relationshipKey);

    if (issues.length === issueCountBefore) {
      relationships.push({
        rowNumber,
        relationType: relationTypeValue as ResourceRelationshipType,
        sourceCode,
        targetCode,
        priority,
        note,
      });
    }
  });

  if (resourceRows.length === 0 && relationshipRows.length === 0 && issues.length === 0) {
    issue(issues, '工作簿', null, 'IMPORT_EMPTY_PAYLOAD', '工作簿中没有可导入的数据行');
  }
  return { payload: { ...control, resources, relationships }, issues };
};

export const parseResourceTopologyImportFile = async (file: File): Promise<ParsedResourceTopologyImport> => {
  if (!file.name.toLowerCase().endsWith('.xlsx')) {
    return {
      payload: { resources: [], relationships: [] },
      issues: [{
        id: '工作簿-file-IMPORT_FILE_TYPE_INVALID-0',
        sheet: '工作簿',
        rowNumber: null,
        constraintCode: 'IMPORT_FILE_TYPE_INVALID',
        message: '只接受 .xlsx 文件',
      }],
    };
  }
  if (file.size > MAX_TOPOLOGY_IMPORT_FILE_BYTES) {
    return {
      payload: { resources: [], relationships: [] },
      issues: [{
        id: '工作簿-file-IMPORT_FILE_TOO_LARGE-0',
        sheet: '工作簿',
        rowNumber: null,
        constraintCode: 'IMPORT_FILE_TOO_LARGE',
        message: '文件不能超过 5 MB',
      }],
    };
  }
  const bytes = await file.arrayBuffer();
  const workbook = XLSX.read(bytes, { type: 'array' });
  return parseResourceTopologyImportWorkbook(workbook);
};

export const buildResourceTopologyImportTemplate = (): ExcelJS.Workbook => {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'MFG8APS';
  const instructionSheet = workbook.addWorksheet('导入说明');
  [
    ['资源与 CIP 拓扑受控导入说明'],
    ['导入方式', '在“导入控制”Sheet 选择 APPEND（只新增）或 REPLACE（按稳定编码/关系覆盖已提供的行，不删除未提供的行）。'],
    ['导入范围', 'FULL 同时导入资源和关系；RESOURCES 仅资源节点；RELATIONSHIPS 仅资源关系。未选范围内不能填写数据。'],
    ['资源类型', RESOURCE_KINDS.join(' / ')],
    ['关系类型', RELATIONSHIP_TYPES.join(' / ')],
    ['生命周期', 'ACTIVE / INACTIVE'],
    ['CIP 回路成员', '“是”只标记设备或组件可由独立的 CIP 回路模型引用；本导入不会创建 CIP 回路或成员关系。'],
    ['物理层级', '使用资源关系 Sheet 的 PARENT_OF，起点为父节点编码，目标为子节点编码。'],
    ['关系优先级', '当前模板只支持 PARENT_OF，优先级必须留空。'],
    ['关系引用', '起点编码和目标编码可引用当前草稿已有节点或本文件资源节点。'],
    ['行数限制', `资源节点和资源关系合计不超过 ${MAX_TOPOLOGY_IMPORT_ROWS} 行。`],
    ['文件安全', '只接受不超过 5 MB 的 .xlsx 文件；禁止公式单元格；Sheet 名称、表头和顺序必须保持不变。编码列必须使用文本格式。'],
  ].forEach((row) => instructionSheet.addRow(row));
  instructionSheet.getColumn(1).width = 18;
  instructionSheet.getColumn(2).width = 88;

  const controlSheet = workbook.addWorksheet(IMPORT_CONTROL_SHEET);
  controlSheet.addRows([
    ['设置项', '值', '说明'],
    ['导入方式', 'APPEND', 'APPEND 只新增；REPLACE 覆盖工作簿中提供的现有行。'],
    ['导入范围', 'FULL', 'FULL / RESOURCES / RELATIONSHIPS。'],
  ]);
  controlSheet.getColumn(1).width = 16;
  controlSheet.getColumn(2).width = 18;
  controlSheet.getColumn(3).width = 72;

  const dictionarySheet = workbook.addWorksheet(IMPORT_DICTIONARY_SHEET);
  const dictionaries = [
    ['导入方式', ...IMPORT_MODES],
    ['资源类型', ...RESOURCE_KINDS],
    ['生命周期', ...LIFECYCLE_STATUSES],
    ['可作为CIP回路成员', '是', '否'],
    ['关系类型', ...RELATIONSHIP_TYPES],
    ['导入范围', ...IMPORT_SCOPES],
  ];
  dictionaries.forEach((values, index) => {
    dictionarySheet.getColumn(index + 1).values = values;
  });
  dictionarySheet.state = 'veryHidden';
  controlSheet.getCell('B2').dataValidation = {
    type: 'list', allowBlank: false, formulae: [`'${IMPORT_DICTIONARY_SHEET}'!$A$2:$A$3`],
  };
  controlSheet.getCell('B3').dataValidation = {
    type: 'list', allowBlank: false, formulae: [`'${IMPORT_DICTIONARY_SHEET}'!$F$2:$F$4`],
  };

  const resourceSheet = workbook.addWorksheet(RESOURCE_IMPORT_SHEET);
  resourceSheet.addRow(RESOURCE_HEADERS);
  RESOURCE_HEADERS.forEach((header, index) => {
    resourceSheet.getColumn(index + 1).width = Math.max(14, header.length * 2);
  });
  resourceSheet.getColumn(1).numFmt = '@';
  resourceSheet.views = [{ state: 'frozen', ySplit: 1 }];
  addDataValidation(resourceSheet, 'A2:A5001', {
    type: 'textLength', operator: 'between', allowBlank: false, formulae: [1, 64],
  });
  addDataValidation(resourceSheet, 'B2:B5001', {
    type: 'textLength', operator: 'between', allowBlank: false, formulae: [1, 120],
  });
  addDataValidation(resourceSheet, 'C2:C5001', {
    type: 'list', allowBlank: false, formulae: [`'${IMPORT_DICTIONARY_SHEET}'!$B$2:$B$9`],
  });
  addDataValidation(resourceSheet, 'D2:D5001', {
    type: 'list', allowBlank: false, formulae: [`'${IMPORT_DICTIONARY_SHEET}'!$C$2:$C$3`],
  });
  addDataValidation(resourceSheet, 'E2:E5001', {
    type: 'list', allowBlank: false, formulae: [`'${IMPORT_DICTIONARY_SHEET}'!$D$2:$D$3`],
  });

  const relationshipSheet = workbook.addWorksheet(RELATIONSHIP_IMPORT_SHEET);
  relationshipSheet.addRow(RELATIONSHIP_HEADERS);
  RELATIONSHIP_HEADERS.forEach((header, index) => {
    relationshipSheet.getColumn(index + 1).width = Math.max(14, header.length * 2);
  });
  relationshipSheet.getColumn(2).numFmt = '@';
  relationshipSheet.getColumn(3).numFmt = '@';
  relationshipSheet.views = [{ state: 'frozen', ySplit: 1 }];
  addDataValidation(relationshipSheet, 'A2:A5001', {
    type: 'list', allowBlank: false, formulae: [`'${IMPORT_DICTIONARY_SHEET}'!$E$2:$E$5`],
  });
  addDataValidation(relationshipSheet, 'B2:C5001', {
    type: 'textLength', operator: 'between', allowBlank: false, formulae: [1, 64],
  });
  addDataValidation(relationshipSheet, 'D2:D5001', {
    type: 'whole', operator: 'between', allowBlank: true, formulae: [1, 999999],
  });

  return workbook;
};

export const downloadResourceTopologyImportTemplate = async (): Promise<void> => {
  const workbook = buildResourceTopologyImportTemplate();
  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(
    new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
    '资源与CIP拓扑受控导入模板.xlsx',
  );
};

export interface ResourceTopologyImportWorkbookExportInput {
  resources: ResourceNode[];
  relationships: ResourceRelationship[];
  mode?: ResourceTopologyImportMode;
  scope?: ResourceTopologyImportScope;
}

const exportCellText = (value: string | null): string => value ?? '';

export const buildResourceTopologyImportWorkbook = (
  input: ResourceTopologyImportWorkbookExportInput,
): ExcelJS.Workbook => {
  const workbook = buildResourceTopologyImportTemplate();
  const mode = input.mode ?? 'REPLACE';
  const scope = input.scope ?? 'FULL';
  const exportableResources = input.resources.filter((resource) => (
    EXPORTABLE_RESOURCE_KINDS.has(resource.kind) && resource.lifecycleStatus !== 'RETIRED'
  ));
  const resourceCodeById = new Map(exportableResources.map((resource) => [resource.id, resource.code]));
  const resourceSheet = workbook.getWorksheet(RESOURCE_IMPORT_SHEET);
  const relationshipSheet = workbook.getWorksheet(RELATIONSHIP_IMPORT_SHEET);
  const controlSheet = workbook.getWorksheet(IMPORT_CONTROL_SHEET);

  if (controlSheet) {
    controlSheet.getCell('B2').value = mode;
    controlSheet.getCell('B3').value = scope;
  }

  exportableResources.forEach((resource) => {
    const cipCleanable = resource.attributes?.cipCleanable === true ? '是' : '否';
    resourceSheet?.addRow([
      resource.code,
      resource.name,
      resource.kind,
      resource.lifecycleStatus,
      cipCleanable,
      exportCellText(resource.note),
    ]);
  });

  input.relationships
    .filter((relationship) => (
      relationship.type === 'PARENT_OF'
      && resourceCodeById.has(relationship.sourceResourceId)
      && resourceCodeById.has(relationship.targetResourceId)
    ))
    .forEach((relationship) => {
      relationshipSheet?.addRow([
        'PARENT_OF',
        resourceCodeById.get(relationship.sourceResourceId),
        resourceCodeById.get(relationship.targetResourceId),
        '',
        exportCellText(relationship.note),
      ]);
    });

  return workbook;
};

export const downloadResourceTopologyImportWorkbook = async (
  input: ResourceTopologyImportWorkbookExportInput,
  fileName = '资源拓扑可覆盖导出.xlsx',
): Promise<void> => {
  const workbook = buildResourceTopologyImportWorkbook(input);
  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(
    new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
    fileName,
  );
};
